import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth/auth-store";
import { ProjectsApi } from "@/services/api/projects";
import { api } from "@/services/api/client";
import { getInvitationAcceptRedirect } from "@/utils/invitationRedirect";

interface UseHomeProjectRedirectResult {
  isRedirecting: boolean;
  performRedirect: () => Promise<void>;
}

type RegistrationContinuationAction = "free" | "checkout";

interface RegistrationContinuationResponse {
  hasIntent: boolean;
  action?: unknown;
  selectedPlan?: {
    tier: "free" | "basic" | "standard" | "pro";
    billingInterval?: "monthly" | "annual";
  };
  billingContext?: {
    orgId: string;
    orgSlug: string;
  };
}

interface SponsorshipAcceptResponse {
  billingOrgName?: string;
  acceptedAt?: string;
  appliedProvisioningConfig?: {
    homeProjectId?: string | null;
    homePageId?: string | null;
  } | null;
  redirectUrl?: string;
}

const STRIPE_CHECKOUT_HOST = "checkout.stripe.com";
const REGISTRATION_CONTINUATION_ACTIONS = new Set<string>([
  "free",
  "checkout",
] satisfies readonly RegistrationContinuationAction[]);
const INVITATION_REDIRECT_EMAIL_KEY = "invitation_redirect_email";
const INVITATION_REDIRECT_KEY = "invitation_redirect";
const SPONSORSHIP_INVITE_TOKEN_PATTERN = /^[a-f0-9]{64}$/i;

function isRegistrationContinuationAction(
  action: unknown,
): action is RegistrationContinuationAction {
  return (
    typeof action === "string" && REGISTRATION_CONTINUATION_ACTIONS.has(action)
  );
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function normalizeEmail(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? "";
}

function maskEmailForInviteDebug(email: string): string {
  const normalizedEmail = normalizeEmail(email);
  const [localPart = "", domain = ""] = normalizedEmail.split("@");
  if (!localPart || !domain) {
    return normalizedEmail ? "[invalid-email]" : "[empty]";
  }

  return `${localPart.slice(0, 2)}***@${domain}`;
}

function getInvitationRedirectDebugType(path: string): string {
  if (path.startsWith("/invitations/accept/")) {
    return "invitation-accept";
  }

  if (path.startsWith("/invitations/")) {
    return "invitation";
  }

  return "unknown";
}

function getStoredSponsorshipInviteToken(path: string): string | null {
  try {
    const redirect = new URL(path, window.location.origin);
    if (
      redirect.origin !== window.location.origin ||
      redirect.pathname.split("/").length !== 4 ||
      !redirect.pathname.startsWith("/invitations/accept/") ||
      redirect.searchParams.get("kind") !== "sponsorship"
    ) {
      return null;
    }

    const pathSegments = redirect.pathname.split("/");
    const encodedToken = pathSegments[pathSegments.length - 1] ?? "";
    const token = decodeURIComponent(encodedToken);
    return SPONSORSHIP_INVITE_TOKEN_PATTERN.test(token) ? token : null;
  } catch {
    return null;
  }
}

function getStoredInvitationRedirectValue(key: string): string | null {
  return sessionStorage.getItem(key);
}

function removeStoredInvitationRedirectValue(key: string): void {
  sessionStorage.removeItem(key);
}

function logSponsorshipInviteRedirectDebug(
  event: string,
  metadata: Record<string, unknown>,
): void {
  console.info(`[sponsorship-invite:redirect] ${event}`, metadata);
}

function redirectToCheckout(rawUrl: string): void {
  const url = new URL(rawUrl, window.location.origin);
  const isStripeCheckout =
    url.protocol === "https:" && url.hostname === STRIPE_CHECKOUT_HOST;
  const isSameOrigin = url.origin === window.location.origin;

  if (!isStripeCheckout && !isSameOrigin) {
    throw new Error("Unexpected billing redirect URL");
  }

  window.location.assign(url.toString());
}

/**
 * Hook for handling login redirection to home project/page.
 *
 * Checks if user has homeProjectId and homePageId set in preferences:
 * - If both set and valid, navigates to /viewer/{homeProjectId}/page/{homePageId}
 * - If only project set and valid, navigates to /viewer/{homeProjectId}
 * - If project set but page invalid, clears homePageId and navigates to /viewer/{homeProjectId}
 * - If project invalid (not found/accessible), clears both and navigates to /dashboard
 * - If neither set, navigates to /dashboard
 */
export const useHomeProjectRedirect = (): UseHomeProjectRedirectResult => {
  const navigate = useNavigate();
  const { clearHomeProject, clearHomePage } = useAuthStore();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const performRedirect = useCallback(async () => {
    // Get fresh state from store to avoid closure issues
    const state = useAuthStore.getState();
    const currentUser = state.user;
    const currentIsAuthenticated = state.isAuthenticated;

    if (!currentIsAuthenticated || !currentUser) {
      return;
    }

    setIsRedirecting(true);

    try {
      let registrationIntentFound = false;
      try {
        const continuation = await api.get<RegistrationContinuationResponse>(
          "/billing/registration-continuation",
        );
        const intent = continuation.data;
        if (intent?.hasIntent) {
          registrationIntentFound = true;
          if (!isRegistrationContinuationAction(intent.action)) {
            throw new Error("Unexpected registration continuation action");
          }

          if (intent.action === "checkout") {
            const checkout = await api.post<
              { url: string },
              { successUrl: string; cancelUrl: string }
            >("/billing/registration-continuation/checkout", {
              successUrl: `${window.location.origin}/billing?session_id={CHECKOUT_SESSION_ID}`,
              cancelUrl: `${window.location.origin}/billing?registrationCheckout=cancelled`,
            });

            if (!checkout.data?.url) {
              throw new Error("Missing checkout URL");
            }

            redirectToCheckout(checkout.data.url);
            return;
          }

          await api.post("/billing/registration-continuation/free", {});
          navigate("/billing");
          return;
        }
      } catch (error) {
        console.error(
          "Failed to continue registration billing intent:",
          getErrorMessage(error, "Registration continuation failed"),
        );
        if (registrationIntentFound) {
          navigate("/billing");
          return;
        }
      }

      const invitationRedirect = getStoredInvitationRedirectValue(
        INVITATION_REDIRECT_KEY,
      );
      if (invitationRedirect) {
        const expectedEmail = normalizeEmail(
          getStoredInvitationRedirectValue(INVITATION_REDIRECT_EMAIL_KEY),
        );
        removeStoredInvitationRedirectValue(INVITATION_REDIRECT_KEY);
        removeStoredInvitationRedirectValue(INVITATION_REDIRECT_EMAIL_KEY);
        const currentEmail = normalizeEmail(currentUser.email);
        logSponsorshipInviteRedirectDebug("stored invite redirect found", {
          redirectType: getInvitationRedirectDebugType(invitationRedirect),
          expectedEmail: maskEmailForInviteDebug(expectedEmail),
          currentEmail: maskEmailForInviteDebug(currentEmail),
          hasExpectedEmail: Boolean(expectedEmail),
          emailMatches: !expectedEmail || currentEmail === expectedEmail,
        });
        if (expectedEmail && currentEmail !== expectedEmail) {
          logSponsorshipInviteRedirectDebug(
            "invite redirect skipped for signed-in user mismatch",
            {
              expectedEmail: maskEmailForInviteDebug(expectedEmail),
              currentEmail: maskEmailForInviteDebug(currentEmail),
            },
          );
          navigate("/dashboard");
          return;
        }

        if (invitationRedirect.startsWith("/invitations/")) {
          const sponsorshipInviteToken =
            getStoredSponsorshipInviteToken(invitationRedirect);
          if (sponsorshipInviteToken) {
            logSponsorshipInviteRedirectDebug(
              "auto-accepting stored sponsorship invite",
              {
                redirectType: getInvitationRedirectDebugType(invitationRedirect),
              },
            );
            const response = await api.post<SponsorshipAcceptResponse>(
              `/sponsorships/token/${sponsorshipInviteToken}/accept`,
            );
            const provisioning = response.data?.appliedProvisioningConfig;
            const redirectPath = getInvitationAcceptRedirect({
              organization: {
                _id: "",
                name: response.data?.billingOrgName ?? "TerraMatrix",
                slug: "account",
              },
              role: "sponsored",
              acceptedAt: response.data?.acceptedAt ?? new Date().toISOString(),
              appliedProvisioningConfig: provisioning?.homeProjectId
                ? {
                    homeProjectId: provisioning.homeProjectId,
                    homePageId: provisioning.homePageId ?? null,
                  }
                : null,
              redirectUrl: response.data?.redirectUrl ?? "/account/people",
            });
            logSponsorshipInviteRedirectDebug(
              "stored sponsorship invite accepted",
              {
                redirectPath,
              },
            );
            navigate(redirectPath);
            return;
          }

          logSponsorshipInviteRedirectDebug("navigating to invite redirect", {
            redirectType: getInvitationRedirectDebugType(invitationRedirect),
          });
          navigate(invitationRedirect);
          return;
        }

        logSponsorshipInviteRedirectDebug("ignored unsafe invite redirect", {
          redirectType: getInvitationRedirectDebugType(invitationRedirect),
        });
      }

      // Check for pending onboarding redirect (set by CustomerOnboardingPage before login)
      const onboardingRedirect = sessionStorage.getItem("onboarding_redirect");
      if (onboardingRedirect) {
        sessionStorage.removeItem("onboarding_redirect");
        if (onboardingRedirect.startsWith("/onboarding/")) {
          navigate(onboardingRedirect);
          return;
        }
      }

      const homeProjectId = currentUser.preferences?.homeProjectId;
      const homePageId = currentUser.preferences?.homePageId;

      // No home project set, go to dashboard
      if (!homeProjectId) {
        navigate("/dashboard");
        return;
      }

      // Validate that the home project exists and is accessible
      try {
        const projectResponse = await ProjectsApi.getProject(homeProjectId);
        const project = projectResponse.data;

        if (!project) {
          throw new Error("Project not found");
        }

        // If home page is set, trust it and navigate
        // Note: The API response may not include page IDs in project.pages (they're empty objects)
        // and project.versions may not be included. Rather than trying to validate,
        // we trust the homePageId since the user explicitly set it.
        // The viewer will handle fallback to default page if the page doesn't exist.
        if (homePageId && homePageId !== "undefined" && homePageId !== "null") {
          navigate(`/viewer/${homeProjectId}/page/${homePageId}`);
        } else {
          // No home page set, navigate to project (viewer will show default page)
          navigate(`/viewer/${homeProjectId}`);
        }
      } catch (_error) {
        // Project doesn't exist or user doesn't have access
        // Clear both home project and page and go to dashboard
        try {
          await clearHomeProject();
        } catch (clearError) {
          console.error(
            "Failed to clear home project:",
            getErrorMessage(clearError, "Failed to clear home project"),
          );
        }
        navigate("/dashboard");
      }
    } catch (_error) {
      // Any other error, clear home project and go to dashboard
      try {
        await clearHomeProject();
      } catch (clearError) {
        console.error(
          "Failed to clear home project:",
          getErrorMessage(clearError, "Failed to clear home project"),
        );
      }
      navigate("/dashboard");
    } finally {
      setIsRedirecting(false);
    }
  }, [navigate, clearHomeProject, clearHomePage]);

  return {
    isRedirecting,
    performRedirect,
  };
};

export default useHomeProjectRedirect;
